import { AppRouteMeta } from '@sennder/shell-utilities'

import { MicrofrontendId } from './microfrontends'
import {
  restrictForCharteringOfficeUsers,
  redirectToSingleViewAP,
  redirectToDefaultPage,
  checkContractManagementV2Access,
  checkShipperContactsAccess,
  restrictToDev,
  redirectToTrip,
  redirectToCustomerOrder,
  redirectToShipment,
  canAcessTransportAnalysis,
} from './route-guards'

/**
 * Unique application route
 */
export type AppRoute = keyof typeof routes

/**
 * All routes of the application, ordered alphabetically by path.
 * Each route has navigation guards, analytical and logging contexts.
 */
export const routes = {
  '/blocklisting-dashboard': {
    name: 'blocklisting-dashboard',
    mf: 'blocklisting',
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/blocklisting/:blocklistingId': {
    name: 'blocklisting-review',
    mf: 'vetting-review',
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/suppliers': {
    name: 'suppliers-dashboard',
    mf: 'suppliers-dashboard',
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/suppliers/vendors/create': {
    name: 'vendor-create',
    mf: 'vendor-create',
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/suppliers/vendors/:id': {
    name: 'vendor-profile',
    mf: 'vendor-profile',
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/suppliers/document/:entity/:id': {
    name: 'suppliers-document-url-resolver',
    mf: 'suppliers-document-url-resolver',
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/carriers': {
    name: 'carrier-search',
    mf: 'carrier-search',
    context: {
      analytics: {
        module: 'carrier-search-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'carrier-search-mf-component',
      },
    },
  },
  '/carriers/:carrierId': {
    name: 'carrier-profile',
    mf: 'carrier-profile',
    context: {
      analytics: {
        module: 'carrier-profile-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'carrier-profile-mf-component',
      },
    },
  },
  '/contract-management': {
    name: 'contract-management',
    mf: 'octopus',
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'contract-management',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'contract-management',
      },
    },
  },
  '/contract-management-mf': {
    name: 'contract-management-mf',
    mf: 'contract-management-mf',
    context: {
      analytics: {
        module: 'contract-management-mf',
        submodule: '',
      },
      logger: {
        codeOwners: 'tendering',
        module: 'contract-management-mf',
      },
    },
    guards: [checkContractManagementV2Access],
  },
  '/consolidation-cargo/:id': {
    name: 'consolidation-cargo',
    mf: 'shipment',
    context: {
      analytics: {
        module: 'shipment-mf-component',
        submodule: 'consolidation-cargo',
      },
      logger: {
        codeOwners: 'ingestion',
        module: 'shipment',
      },
    },
  },
  '/dashboard': {
    name: 'dashboard',
    mf: 'octopus',
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'dashboard',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'dashboard',
      },
    },
    guards: [redirectToDefaultPage],
  },
  // Legacy route, only redirect is supported
  '/dashboard/finance': {
    name: 'finance-dashboard',
    mf: 'octopus',
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'finance-dashboard',
      },
      logger: {
        codeOwners: 'settlement',
        module: 'finance-dashboard',
      },
    },
    guards: [redirectToSingleViewAP],
  },
  '/facilities': {
    name: 'facility-management',
    mf: 'facility-management',
    context: {
      analytics: {
        module: 'facility-management-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'facility-management',
        module: 'facility-management-mf-component',
      },
    },
  },
  '/analysis': {
    name: 'analysis-board',
    mf: 'analysis',
    context: {
      analytics: {
        module: 'analysis-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'analysis-board',
        module: 'analysis-mf-component',
      },
    },
  },
  // Legacy route, only redirect is supported
  '/finance/ordering/:orderGroupId(\\d+):letter([A-Z]+)': {
    name: 'finance-ordering',
    mf: 'octopus',
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'finance-ordering',
      },
      logger: {
        codeOwners: 'settlement',
        module: 'finance-ordering',
      },
    },
    guards: [redirectToTrip],
  },
  // Legacy route, only redirect is supported
  '/finance/ordering/:orderGroupId(\\d+):letter([A-Z]+)/:financeView': {
    name: 'finance-view',
    mf: 'octopus',
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'finance-view',
      },
      logger: {
        codeOwners: 'settlement',
        module: 'finance-view',
      },
    },
    guards: [redirectToTrip],
  },
  '/login': {
    name: 'login',
    mf: 'auth',
    context: {
      analytics: {
        module: 'internal-auth-mf-component-v2',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-core',
        module: 'internal-auth-mf-component-v2',
      },
    },
    fullscreenLayout: true,
    public: true,
  },
  '/ordering/:orderGroupId(\\d+):letter([A-Z]+)': {
    name: 'ordering',
    mf: 'octopus',
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'ordering',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'ordering',
      },
    },
    guards: [redirectToTrip],
  },
  '/order': {
    name: 'ordering-legacy-redirect',
    mf: 'octopus',
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'ordering-legacy-redirect',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'ordering-legacy-redirect',
      },
    },
  },
  '/planner': {
    name: 'planner',
    mf: 'planner',
    context: {
      analytics: {
        module: 'planner',
        submodule: '',
      },
      logger: {
        codeOwners: 'asset-planning',
        module: 'planner-mf-component',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/contracts': {
    name: 'contracts',
    mf: 'contracts',
    context: {
      analytics: {
        module: 'contracts',
        submodule: '',
      },
      logger: {
        codeOwners: 'asset-planning',
        module: 'chartering-contracts',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/marketplace': {
    name: 'marketplace',
    mf: 'marketplace',
    context: {
      analytics: {
        module: 'marketplace',
        submodule: '',
      },
      logger: {
        codeOwners: 'pod-discovery',
        module: 'marketplace-mf-component',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/order-details/:idForStaff': {
    name: 'order-details',
    mf: 'marketplace',
    context: {
      analytics: {
        module: 'order-details',
        submodule: '',
      },
      logger: {
        module: 'marketplace-mf-component',
        codeOwners: 'pod-discovery',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/senn-teams': {
    name: 'senn-teams',
    mf: 'sennteams',
    context: {
      analytics: {
        module: 'sennteams-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'sennteams-mf-component',
      },
    },
  },
  '/shipper-contacts': {
    name: 'shipper-contacts',
    mf: 'shipper-contacts',
    context: {
      analytics: {
        module: 'shipper-contacts-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'pod-portal',
        module: 'shipper-contacts-mf-component',
      },
    },
    guards: [checkShipperContactsAccess],
  },
  '/customer-data': {
    name: 'customer-data',
    mf: 'customer-data',
    context: {
      analytics: {
        module: 'customer-data-mf-components',
        submodule: '',
      },
      logger: {
        codeOwners: 'pod-portal',
        module: 'customer-data-mf-components',
      },
    },
  },
  '/customer-order/:id': {
    name: 'customer-order',
    mf: 'shipment',
    context: {
      analytics: {
        module: 'shipment-mf-component',
        submodule: 'customer-order',
      },
      logger: {
        codeOwners: 'ingestion',
        module: 'shipment',
      },
    },
    guards: [redirectToShipment],
  },
  '/sennlab': {
    name: 'sennlab',
    mf: 'sennlab',
    context: {
      analytics: {
        module: 'sennlab-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'sennlab-mf-component',
      },
    },
    guards: [restrictToDev],
  },
  '/shipment/:id': {
    name: 'shipment',
    mf: 'shipment',
    context: {
      analytics: {
        module: 'shipment-mf-component',
        submodule: 'shipment',
      },
      logger: {
        codeOwners: 'ingestion',
        module: 'shipment',
      },
    },
    guards: [redirectToCustomerOrder],
  },
  '/single-view': {
    name: 'single-view',
    mf: 'single-view',
    context: {
      analytics: {
        module: 'single-view-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'single-view-mf-component',
      },
    },
  },
  '/trips/:id': {
    name: 'trip',
    mf: 'trip',
    context: {
      analytics: {
        module: 'trip-mf',
        submodule: '',
      },
      logger: {
        codeOwners: 'trip',
        module: 'trip-mf',
      },
    },
  },
  '/spotter': {
    name: 'spotter',
    mf: 'spotter',
    context: {
      analytics: {
        module: 'spotter-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'shipper-integrations',
        module: 'spotter-mf-component',
      },
    },
  },
  '/task-allocation': {
    name: 'task-allocation',
    mf: 'task-allocation',
    context: {
      analytics: {
        module: 'task-allocation-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'task-allocation-mf-component',
      },
    },
  },
  '/vetting-dashboard': {
    name: 'vetting-dashboard',
    mf: 'vetting-dashboard',
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/vetting/:vettingId': {
    name: 'vetting-review',
    mf: 'vetting-review',
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/archive/:orderId': {
    name: 'archived-order',
    mf: 'archived-order',
    context: {
      analytics: {
        module: 'archived-order-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'archived-order-mf-component',
      },
    },
  },
  '/tender-opportunities': {
    name: 'opportunities',
    mf: 'tendering',
    context: {
      analytics: {
        module: 'tendering-mf-component',
        submodule: 'opportunities',
      },
      logger: {
        codeOwners: 'tendering',
        module: 'tendering-mf-component',
      },
    },
  },
  '/tender-analytics': {
    name: 'analytics',
    mf: 'tendering',
    context: {
      analytics: {
        module: 'tendering-mf-component',
        submodule: 'analytics',
      },
      logger: {
        codeOwners: 'tendering',
        module: 'tendering-mf-component',
      },
    },
  },
  '/users': {
    name: 'user-profile',
    mf: 'user-profile',
    context: {
      analytics: {
        module: 'user-profile-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-care',
        module: 'user-profile-mf-component',
      },
    },
    guards: [restrictToDev],
  },
  '/transport-analysis': {
    name: 'transport-analysis',
    mf: 'transport-analysis-ui',
    context: {
      analytics: {
        module: 'transport-analysis-ui',
        submodule: '',
      },
      logger: {
        codeOwners: 'Global Commercial',
        module: 'transport-analysis-ui',
      },
    },
    guards: [canAcessTransportAnalysis],
  },
  '/finance-chat': {
    name: 'finance-chat',
    mf: 'finance-chat',
    context: {
      analytics: {
        module: 'finance-chat',
        submodule: '',
      },
      logger: {
        codeOwners: 'Cloud Platform Team',
        module: 'finance-chat',
      },
    },
    guards: [restrictToDev],
  },
  '/corridors': {
    name: 'corridor-accountability',
    mf: 'corridor-accountability',
    context: {
      analytics: {
        module: 'corridor-accountability-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'corridor-accountability-mf-component',
      },
    },
  },
  '/experiments': {
    name: 'experiments',
    mf: 'experiments',
    context: {
      analytics: {
        module: 'experiments',
        submodule: '',
      },
      logger: {
        codeOwners: 'Cloud Platform Team',
        module: 'experiments',
      },
    },
  },
} as const satisfies {
  [TRoute: string]: AppRouteMeta<MicrofrontendId>
}
